/**
 * Credit & Escrow — Financial Cockpit
 *
 * Four sections:
 *   1. Loan Status Card — current loan state, principal, rate, tenure, apply button
 *   2. Escrow Ledger    — vendor-payment table + balance bar
 *   3. Interest Summary  — principal outstanding, interest accrued, total payable, est. deduction
 *   4. Settlement Projection — estimated harvest, loan deduction, net payout
 *
 * All data comes from existing creditAPI + escrowAPI.  No backend changes.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  CheckCircle,
  Schedule,
  TrendingUp,
  AttachMoney,
  Receipt,
  Warning,
  Add,
  Close,
  Refresh,
  Assessment,
  Gavel,
  Agriculture,
  LocalShipping,
  MedicalServices,
  Category,
} from '@mui/icons-material';
import { useAuth } from 'hooks/useAuth';
import { creditAPI, escrowAPI, pondsAPI } from 'services/api';

// ─── Loan status helpers ────────────────────────────────────
const STATUS_CONFIG = {
  pending:      { label: 'Under Review',  color: 'warning',  icon: <Schedule />,      bg: '#fff3e0' },
  under_review: { label: 'Under Review',  color: 'warning',  icon: <Schedule />,      bg: '#fff3e0' },
  approved:     { label: 'Approved',       color: 'info',     icon: <CheckCircle />,   bg: '#e3f2fd' },
  disbursed:    { label: 'Active',         color: 'success',  icon: <TrendingUp />,    bg: '#e8f5e9' },
  repaid:       { label: 'Closed',         color: 'default',  icon: <CheckCircle />,   bg: '#f5f5f5' },
  settled:      { label: 'Settled',        color: 'default',  icon: <CheckCircle />,   bg: '#f5f5f5' },
  defaulted:    { label: 'Defaulted',      color: 'error',    icon: <Warning />,       bg: '#ffebee' },
  rejected:     { label: 'Rejected',       color: 'error',    icon: <Warning />,       bg: '#ffebee' },
  cancelled:    { label: 'Cancelled',      color: 'default',  icon: <Close />,         bg: '#f5f5f5' },
};

const ENTRY_TYPE_LABELS = {
  CREDIT_IN:            'Credit Disbursement',
  VENDOR_PAYMENT:       'Vendor Payment',
  SETTLEMENT_IN:        'Settlement Deposit',
  PRINCIPAL_DEDUCTION:  'Principal Deduction',
  INTEREST_DEDUCTION:   'Interest Deduction',
  INSURANCE_DEDUCTION:  'Insurance Deduction',
  FARMER_PAYOUT:        'Farmer Payout',
  PREMIUM_IN:           'Premium Payment',
};

const CATEGORY_ICON = {
  VENDOR_PAYMENT:      <LocalShipping sx={{ fontSize: 16 }} />,
  CREDIT_IN:           <AccountBalanceIcon sx={{ fontSize: 16 }} />,
  SETTLEMENT_IN:       <Agriculture sx={{ fontSize: 16 }} />,
  PRINCIPAL_DEDUCTION: <Gavel sx={{ fontSize: 16 }} />,
  INTEREST_DEDUCTION:  <TrendingUp sx={{ fontSize: 16 }} />,
  INSURANCE_DEDUCTION: <Assessment sx={{ fontSize: 16 }} />,
  FARMER_PAYOUT:       <AttachMoney sx={{ fontSize: 16 }} />,
  PREMIUM_IN:          <Receipt sx={{ fontSize: 16 }} />,
};

const fmt = (val) =>
  parseFloat(val || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// ═════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════
const CreditEscrowPage = () => {
  const { user } = useAuth();

  // Data state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [applications, setApplications] = useState([]);
  const [activeLoan, setActiveLoan] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [ledgerBalance, setLedgerBalance] = useState(null);
  const [repaymentSchedule, setRepaymentSchedule] = useState(null);
  const [outstanding, setOutstanding] = useState(null);
  const [ponds, setPonds] = useState([]);

  // Apply dialog
  const [applyOpen, setApplyOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyForm, setApplyForm] = useState({
    pond_id: '',
    loan_type: 'crop_investment',
    requested_amount: '',
    purpose: '',
    tenure_months: 12,
  });

  // ── Load all data ─────────────────────────────────────────
  const loadData = useCallback(async () => {
    if (!user) return;
    try {
      setLoading(true);
      setError(null);

      // 1) Credit applications
      const appsRes = await creditAPI.getApplications();
      const apps = appsRes.data || [];
      setApplications(apps);

      // Find primary active loan (disbursed > approved > pending > under_review)
      const priority = ['disbursed', 'approved', 'under_review', 'pending'];
      let primary = null;
      for (const s of priority) {
        primary = apps.find((a) => a.status === s);
        if (primary) break;
      }
      // Fallback to most recent if no active
      if (!primary && apps.length > 0) {
        primary = apps[0]; // already ordered desc by applied_date
      }
      setActiveLoan(primary);

      // 2) Ponds for apply form
      const pondsRes = await pondsAPI.getAllForUser();
      setPonds(pondsRes || []);

      // 3) If we have an active loan, fetch ledger + schedule + outstanding
      if (primary) {
        const promises = [];

        // Escrow ledger entries
        promises.push(
          escrowAPI
            .getLedgerEntries(primary.id)
            .then((r) => setLedgerEntries(r.data || []))
            .catch(() => setLedgerEntries([])),
        );

        // Escrow balance
        promises.push(
          escrowAPI
            .getLedgerBalance(primary.id)
            .then((r) => setLedgerBalance(r.data || null))
            .catch(() => setLedgerBalance(null)),
        );

        // Repayment schedule
        promises.push(
          creditAPI
            .getRepaymentSchedule(primary.id)
            .then((r) => setRepaymentSchedule(r.data || null))
            .catch(() => setRepaymentSchedule(null)),
        );

        // Outstanding
        promises.push(
          creditAPI
            .getOutstanding(primary.id)
            .then((r) => setOutstanding(r.data || null))
            .catch(() => setOutstanding(null)),
        );

        await Promise.allSettled(promises);
      }
    } catch (err) {
      console.error('Credit cockpit load error:', err);
      setError('Failed to load credit data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Apply for loan ────────────────────────────────────────
  const handleApply = async (e) => {
    e.preventDefault();
    try {
      setApplying(true);
      setError(null);
      const payload = {
        ...applyForm,
        pond_id: applyForm.pond_id ? parseInt(applyForm.pond_id) : null,
        requested_amount: parseFloat(applyForm.requested_amount),
        tenure_months: parseInt(applyForm.tenure_months),
      };
      await creditAPI.createApplication(payload);
      setApplyOpen(false);
      setApplyForm({ pond_id: '', loan_type: 'crop_investment', requested_amount: '', purpose: '', tenure_months: 12 });
      loadData();
    } catch (err) {
      console.error('Apply error:', err);
      const msg = err.response?.data?.detail;
      setError(typeof msg === 'string' ? msg : 'Failed to submit loan application.');
    } finally {
      setApplying(false);
    }
  };

  // ── Derived calculations ──────────────────────────────────
  const principalAmount = activeLoan?.approved_amount || activeLoan?.disbursed_amount || activeLoan?.requested_amount || 0;
  const interestRate = activeLoan?.interest_rate || 0;
  const tenure = activeLoan?.approved_tenure || activeLoan?.tenure_months || 0;

  // Interest: Simple interest for display
  const interestAccrued = repaymentSchedule
    ? (repaymentSchedule.interest_amount || 0)
    : (principalAmount * interestRate * tenure) / (100 * 12);
  const totalPayable = repaymentSchedule
    ? (repaymentSchedule.total_amount || 0)
    : principalAmount + interestAccrued;
  const outstandingBalance = outstanding?.outstanding || 0;

  // Escrow balance
  const escrowBal = ledgerBalance?.current_balance ? parseFloat(ledgerBalance.current_balance) : 0;
  const totalDisbursed = activeLoan?.disbursed_amount || 0;
  const balanceUsedPct = totalDisbursed > 0 ? Math.min(((totalDisbursed - escrowBal) / totalDisbursed) * 100, 100) : 0;

  // Settlement projection (estimate)
  const estimatedHarvestValue = escrowBal > 0 ? escrowBal * 2.5 : principalAmount * 2; // rough estimate
  const loanDeduction = totalPayable;
  const netPayout = Math.max(estimatedHarvestValue - loanDeduction, 0);

  const hasActiveLoan = activeLoan && !['rejected', 'cancelled', 'repaid', 'settled'].includes(activeLoan.status);
  const canApply = !hasActiveLoan;
  const statusCfg = activeLoan ? (STATUS_CONFIG[activeLoan.status] || STATUS_CONFIG.pending) : null;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* ── Page Header ───────────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <AccountBalanceIcon sx={{ color: '#9c27b0', fontSize: 32 }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Credit &amp; Escrow
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Financial cockpit — loans, escrow, and settlement projections
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Refresh />}
          onClick={loadData}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ═══════════════════════════════════════════════════════
          SECTION 1 — Loan Status Card
          ═══════════════════════════════════════════════════════ */}
      <Card
        sx={{
          mb: 3,
          background: statusCfg
            ? `linear-gradient(135deg, ${statusCfg.bg} 0%, #ffffff 100%)`
            : 'linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceIcon color="primary" />
              Loan Status
            </Typography>
            {activeLoan ? (
              <Chip
                icon={statusCfg.icon}
                label={statusCfg.label}
                color={statusCfg.color}
                sx={{ fontWeight: 600 }}
              />
            ) : (
              <Chip label="Not Applied" variant="outlined" />
            )}
          </Box>

          {activeLoan ? (
            <Grid container spacing={3}>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Application #
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {activeLoan.application_number || `#${activeLoan.id}`}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Principal
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ₹{fmt(principalAmount)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Interest Rate
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {interestRate}% p.a.
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Tenure
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {tenure} months
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Loan Type
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                  {(activeLoan.loan_type || '').replace(/_/g, ' ')}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <Typography variant="caption" color="text.secondary">
                  Applied Date
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {activeLoan.applied_date
                    ? new Date(activeLoan.applied_date).toLocaleDateString()
                    : '—'}
                </Typography>
              </Grid>
              {activeLoan.disbursed_at && (
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Sanction Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {new Date(activeLoan.disbursed_at).toLocaleDateString()}
                  </Typography>
                </Grid>
              )}
              {activeLoan.repayment_type && (
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Repayment
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {activeLoan.repayment_type}
                  </Typography>
                </Grid>
              )}
              {activeLoan.risk_level && (
                <Grid item xs={6} sm={4} md={2}>
                  <Typography variant="caption" color="text.secondary">
                    Risk Level
                  </Typography>
                  <Chip
                    label={activeLoan.risk_level}
                    size="small"
                    color={
                      activeLoan.risk_level === 'low'
                        ? 'success'
                        : activeLoan.risk_level === 'high'
                          ? 'error'
                          : 'warning'
                    }
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Grid>
              )}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <AccountBalanceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No active loan application found.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Apply for a credit facility to fund your aquaculture operations.
              </Typography>
            </Box>
          )}

          {/* Apply button */}
          {canApply && (
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => setApplyOpen(true)}
                sx={{
                  background: 'linear-gradient(135deg, #9c27b0, #7b1fa2)',
                  fontWeight: 600,
                  borderRadius: 2,
                  '&:hover': { background: 'linear-gradient(135deg, #7b1fa2, #6a1b9a)' },
                }}
              >
                Apply for Loan
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════
          SECTION 2 — Escrow Ledger
          ═══════════════════════════════════════════════════════ */}
      {activeLoan && (
        <Card sx={{ mb: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Receipt color="primary" />
              Escrow Ledger
            </Typography>

            {/* Balance bar */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  Remaining Balance
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  ₹{fmt(escrowBal)} {totalDisbursed > 0 && `/ ₹${fmt(totalDisbursed)}`}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={100 - balanceUsedPct}
                sx={{
                  height: 12,
                  borderRadius: 6,
                  bgcolor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 6,
                    background:
                      escrowBal > totalDisbursed * 0.5
                        ? 'linear-gradient(90deg, #4caf50, #66bb6a)'
                        : escrowBal > totalDisbursed * 0.2
                          ? 'linear-gradient(90deg, #ff9800, #ffa726)'
                          : 'linear-gradient(90deg, #f44336, #ef5350)',
                  },
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {balanceUsedPct.toFixed(0)}% utilized
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(100 - balanceUsedPct).toFixed(0)}% remaining
                </Typography>
              </Box>
            </Box>

            {/* Ledger table */}
            {ledgerEntries.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No escrow transactions yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Type</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Category</TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Reference</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>
                        Amount
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>Flow</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ledgerEntries.map((entry) => {
                      const isInflow = entry.debit_account === 'ESCROW';
                      return (
                        <TableRow key={entry.id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {CATEGORY_ICON[entry.entry_type] || <Category sx={{ fontSize: 16 }} />}
                              <Typography variant="body2">
                                {ENTRY_TYPE_LABELS[entry.entry_type] || entry.entry_type}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.entry_type?.replace(/_/g, ' ')}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.7rem', height: 22, textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="caption" color="text.secondary">
                              {entry.reference_id || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: isInflow ? 'success.main' : 'error.main',
                              }}
                            >
                              {isInflow ? '+' : '−'}₹{fmt(entry.amount)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isInflow ? 'IN' : 'OUT'}
                              size="small"
                              color={isInflow ? 'success' : 'error'}
                              sx={{ fontSize: '0.65rem', height: 20, fontWeight: 700 }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════
          SECTION 3 & 4 — Interest Summary + Settlement Projection
          ═══════════════════════════════════════════════════════ */}
      {activeLoan && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {/* ── Section 3: Interest Summary ─────────────────── */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <TrendingUp color="warning" />
                  Interest Summary
                </Typography>

                {/* Principal outstanding */}
                <Box sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Principal Outstanding
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      ₹{fmt(outstandingBalance || principalAmount)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={
                      principalAmount > 0
                        ? Math.min(((outstandingBalance || principalAmount) / principalAmount) * 100, 100)
                        : 0
                    }
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 4,
                        bgcolor: 'warning.main',
                      },
                    }}
                  />
                </Box>

                {/* Interest accrued */}
                <Box sx={{ mb: 2.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Interest Accrued
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 700, color: 'error.main' }}>
                      ₹{fmt(interestAccrued)}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalPayable > 0 ? Math.min((interestAccrued / totalPayable) * 100, 100) : 0}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      bgcolor: 'grey.200',
                      '& .MuiLinearProgress-bar': { borderRadius: 4, bgcolor: 'error.main' },
                    }}
                  />
                </Box>

                <Divider sx={{ my: 2 }} />

                {/* Total payable */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    Total Payable
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    ₹{fmt(totalPayable)}
                  </Typography>
                </Box>

                {/* Estimated settlement deduction */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">
                    Est. Settlement Deduction
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    ₹{fmt(totalPayable)}
                  </Typography>
                </Box>

                {repaymentSchedule && (
                  <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="caption" color="text.secondary">Repayment Progress</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                      <Typography variant="body2">
                        {repaymentSchedule.paid_installments} / {repaymentSchedule.total_installments} installments
                      </Typography>
                      <Typography variant="body2" color="success.main" fontWeight={600}>
                        ₹{fmt(repaymentSchedule.total_paid)} paid
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={
                        repaymentSchedule.total_installments > 0
                          ? (repaymentSchedule.paid_installments / repaymentSchedule.total_installments) * 100
                          : 0
                      }
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        mt: 1,
                        bgcolor: 'grey.300',
                        '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: 'success.main' },
                      }}
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* ── Section 4: Settlement Projection ───────────── */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                  <Assessment color="success" />
                  Settlement Projection
                </Typography>

                {/* Estimated harvest */}
                <Box
                  sx={{
                    p: 2,
                    mb: 2,
                    borderRadius: 2,
                    bgcolor: '#e8f5e9',
                    border: '1px solid #c8e6c9',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Estimated Harvest Value
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.dark' }}>
                    ₹{fmt(estimatedHarvestValue)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Based on current escrow &amp; historical data
                  </Typography>
                </Box>

                {/* Waterfall */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">
                      Harvest Value
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      ₹{fmt(estimatedHarvestValue)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px dashed', borderColor: 'divider' }}>
                    <Typography variant="body2" color="error.main">
                      (−) Loan Deduction
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>
                      −₹{fmt(loanDeduction)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1.5, mt: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Net Projected Payout
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: netPayout > 0 ? 'success.main' : 'error.main' }}>
                      ₹{fmt(netPayout)}
                    </Typography>
                  </Box>
                </Box>

                {/* Visual bar */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                    Payout Breakdown
                  </Typography>
                  <Box sx={{ display: 'flex', height: 24, borderRadius: 12, overflow: 'hidden', bgcolor: 'grey.200' }}>
                    {estimatedHarvestValue > 0 && (
                      <>
                        <Box
                          sx={{
                            width: `${(loanDeduction / estimatedHarvestValue) * 100}%`,
                            bgcolor: 'error.main',
                            transition: 'width 0.3s',
                          }}
                        />
                        <Box
                          sx={{
                            width: `${(netPayout / estimatedHarvestValue) * 100}%`,
                            bgcolor: 'success.main',
                            transition: 'width 0.3s',
                          }}
                        />
                      </>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'error.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Loan ({estimatedHarvestValue > 0 ? ((loanDeduction / estimatedHarvestValue) * 100).toFixed(0) : 0}%)
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: 'success.main' }} />
                      <Typography variant="caption" color="text.secondary">
                        Payout ({estimatedHarvestValue > 0 ? ((netPayout / estimatedHarvestValue) * 100).toFixed(0) : 0}%)
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                {netPayout <= 0 && (
                  <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                    <Typography variant="body2">
                      <strong>Warning:</strong> Projected payout is negative. Settlement amount may not cover the full loan.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ── Past applications (if any) ────────────────────── */}
      {applications.length > 1 && (
        <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Application History
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>App #</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Requested</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>Approved</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Applied</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {applications.map((app) => {
                    const cfg = STATUS_CONFIG[app.status] || STATUS_CONFIG.pending;
                    return (
                      <TableRow
                        key={app.id}
                        hover
                        sx={app.id === activeLoan?.id ? { bgcolor: 'primary.50' } : {}}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {app.application_number || `#${app.id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                            {(app.loan_type || '').replace(/_/g, ' ')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">₹{fmt(app.requested_amount)}</TableCell>
                        <TableCell align="right">
                          {app.approved_amount ? `₹${fmt(app.approved_amount)}` : '—'}
                        </TableCell>
                        <TableCell>
                          <Chip label={cfg.label} color={cfg.color} size="small" />
                        </TableCell>
                        <TableCell>
                          {app.applied_date ? new Date(app.applied_date).toLocaleDateString() : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════
          Apply for Loan Dialog
          ═══════════════════════════════════════════════════════ */}
      <Dialog open={applyOpen} onClose={() => setApplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, #9c27b0, #7b1fa2)',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AccountBalanceIcon />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Apply for Loan
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                Credit facility for your aquaculture operations
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setApplyOpen(false)} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleApply}>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Loan Type *</InputLabel>
                  <Select
                    value={applyForm.loan_type}
                    onChange={(e) => setApplyForm({ ...applyForm, loan_type: e.target.value })}
                    label="Loan Type *"
                    required
                  >
                    <MenuItem value="crop_investment">Crop Investment</MenuItem>
                    <MenuItem value="input_purchase">Input Purchase</MenuItem>
                    <MenuItem value="equipment">Equipment</MenuItem>
                    <MenuItem value="operational">Operational</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Requested Amount (₹) *"
                  type="number"
                  value={applyForm.requested_amount}
                  onChange={(e) => setApplyForm({ ...applyForm, requested_amount: e.target.value })}
                  required
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  label="Tenure (months) *"
                  type="number"
                  value={applyForm.tenure_months}
                  onChange={(e) => setApplyForm({ ...applyForm, tenure_months: e.target.value })}
                  required
                  inputProps={{ min: 1, max: 60 }}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Linked Pond (optional)</InputLabel>
                  <Select
                    value={applyForm.pond_id}
                    onChange={(e) => setApplyForm({ ...applyForm, pond_id: e.target.value })}
                    label="Linked Pond (optional)"
                  >
                    <MenuItem value="">None</MenuItem>
                    {ponds.map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {p.pond_name || p.name} — {p.pond_area || p.area} ha
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Purpose *"
                  multiline
                  rows={3}
                  value={applyForm.purpose}
                  onChange={(e) => setApplyForm({ ...applyForm, purpose: e.target.value })}
                  placeholder="Describe the purpose of this loan..."
                  required
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, bgcolor: '#f8f9fa' }}>
            <Button variant="outlined" onClick={() => setApplyOpen(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={applying}
              sx={{
                background: 'linear-gradient(135deg, #9c27b0, #7b1fa2)',
                fontWeight: 600,
                '&:hover': { background: 'linear-gradient(135deg, #7b1fa2, #6a1b9a)' },
              }}
            >
              {applying ? <CircularProgress size={20} color="inherit" /> : 'Submit Application'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default CreditEscrowPage;
